import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import { mapFilterRowsToQuery } from "helpers/filters";
import { fetchSavedFilters, getSavedFilters } from "store/savedFiltersSlice";
import { useTranslation } from "translation/useTranslation";
import ErrorAlert from "components/ui/ErrorAlert";
import Link from "components/ui/Link";
import { periodActivityTranslationFallback } from "helpers/periodActivityTranslations";
import { getLang } from "store/userSlice";
import { Button, DateInput, Field, Input, Select } from "components/ui";

const schema = yup.object({
  filters: yup.array().of(yup.object().shape({
    key: yup.object().required().label('Field'),
    operator: yup.object().required().label('Operator'),
    value: yup.string().notRequired().label('Value')
  })).min(1).required()
});

// Self-contained, reusable employee filters-box. Mirrors the batch-update
// filters-box (field / operator / value condition rows, add-condition, apply)
// without the saved/system-filter picker. On apply it drops incomplete rows,
// requires at least one complete condition, and hands the mapped
// { key, operator, value }[] query up via onApply.
export default function EmployeeFilterBox({ onApply, defaultRows, applyLabel, loading = false }) {
  const { translate, editableTranslate } = useTranslation();
  const dispatch = useDispatch();

  const lang = useSelector(getLang);

  const savedFilters = useSelector(getSavedFilters);

  const [filterFields, setFilterFields] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [filterOperators, setFilterOperators] = useState([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState(null);
  const [error, setError] = useState(null);

  const { control, register, reset, getValues, trigger, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      filters: defaultRows?.length ? defaultRows : [{ value: '' }]
    }
  });

  const { fields, append, remove } = useFieldArray({ control, name: "filters" });

  const watchFilters = useWatch({ control, name: 'filters' });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const fetchAvailableFilters = async () => {
    try {
      const { data, status } = await api.get('/employees/available-filters');
      if (status === 200) {
        setFilterFields(data.data);

        const operators = [];
        const fOptions = data.data.map(f => {
          f.operators.forEach(opt => {
            if (!operators.some(o => o.key === opt.key)) operators.push(opt);
          });
          return {
            label: f?.custom_name || translate(f.translation_key, periodActivityTranslationFallback(f.translation_key, f.label)),
            value: f.key
          };
        });

        setFieldOptions(fOptions);
        setFilterOperators(operators);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch available filters'));
      Sentry.captureException(err);
    }
  };

  useEffect(() => {
    fetchAvailableFilters();
    if (!savedFilters) dispatch(fetchSavedFilters());
  }, []);

  // Saved filters the user can load into the box - system filters are excluded.
  const savedFilterOptions = useMemo(() => {
    return (savedFilters || [])
      .filter(s => s.type !== 'system')
      .map(s => ({ value: s.hash_id, label: editableTranslate(s.translation_key, s.name) }));
  }, [savedFilters]);

  const handleSelectSaved = (val) => {
    setSelectedSavedFilter(val);

    if (!val) {
      reset({ filters: [{ value: '' }] });
      return;
    }

    const selected = (savedFilters || []).find(s => s.hash_id === val.value);
    if (!selected) return;

    const rows = selected.filters.map(filter => {
      const opt = filterOperators.find(o => o.key === filter.operator);
      return {
        key: fieldOptions.find(o => o.value === filter.key) || null,
        operator: opt ? { label: editableTranslate(opt.translation_key, opt.label), value: opt.key } : null,
        value: filter.value
      };
    });
    reset({ filters: rows.length ? rows : [{ value: '' }] });
  };

  const getFilterField = (index) => {
    const key = watchFilters?.[index]?.key?.value;
    if (!key) return null;
    return filterFields.find(f => f.key === key) || null;
  };

  const getOperatorOptions = (index) => {
    const key = watchFilters?.[index]?.key?.value;
    if (!key) return [];
    const field = filterFields.find(f => f.key === key);
    return (field?.operators || []).map(o => ({
      label: editableTranslate(o.translation_key, o.label),
      value: o.key
    }));
  };

  const getOperatorValue = (index, value) => {
    if (watchFilters?.[index]?.key && value) {
      const filtered = getOperatorOptions(index).filter(o => o.value === value.value);
      if (filtered.length > 0) return filtered[0];
    }
    return null;
  };

  const handleRemoveRow = (index) => {
    if (fields.length > 1) remove(index);
  };

  const handleApply = async () => {
    const rows = getValues('filters');
    const query = mapFilterRowsToQuery(rows);

    // Require at least one complete condition; surface validation otherwise.
    if (query.length === 0) {
      await trigger('filters');
      return;
    }

    onApply(query, rows);
  };

  return (
    <div className="gray-box filters-box">
      {error && <div className="p-3"><ErrorAlert error={error} onClose={() => setError(null)} /></div>}
      <label className="question-label font-weight-bold d-flex align-items-center justify-content-between">
        {editableTranslate('action.custom.filter_title', 'Beschäftigte filtern')}
        <div className="d-flex align-items-center">
          <span className="mr-2">{editableTranslate('batch_update.filters')}:</span>
          <div style={{ display: 'contents' }} data-testid="saved-filter-select">
            <Select className="min-w-300" options={savedFilterOptions} value={selectedSavedFilter} isClearable={true} onChange={handleSelectSaved} placeholder={editableTranslate('form.select_placeholder')} />
          </div>
        </div>
      </label>
      <div className="px-3 py-2 pb-3">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="form-row filters-table-row">
            <div className="col"><label className="peb-field__label">{editableTranslate('batch_update.field')}</label></div>
            <div className="col"><label className="peb-field__label">{editableTranslate('batch_update.operator')}</label></div>
            <div className="col"><label className="peb-field__label">{editableTranslate('balance_scorecard.value')}</label></div>
          </div>
          {fields.map((f, index) => (
            <div key={f.id} className="form-row">
              <div className="form-group col">
                <Controller
                  name={`filters.${index}.key`}
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => <div style={{ display: 'contents' }} data-testid="filter-field-select">
                    <Select error={!!errors?.filters?.[index]?.key} options={fieldOptions} isClearable={true} onChange={onChange} onBlur={onBlur} ref={ref} value={value ? fieldOptions.find(o => o.value === value.value) : null} />
                  </div>}
                />
                {errors?.filters?.[index]?.key && <div className="peb-field__error">{errors.filters[index].key.message}</div>}
              </div>
              <div className="form-group col">
                <Controller
                  name={`filters.${index}.operator`}
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => <div style={{ display: 'contents' }} data-testid="filter-operator-select">
                    <Select error={!!errors?.filters?.[index]?.operator} options={getOperatorOptions(index)} isClearable={true} onChange={onChange} onBlur={onBlur} ref={ref} value={getOperatorValue(index, value)} />
                  </div>}
                />
                {errors?.filters?.[index]?.operator && <div className="peb-field__error">{errors.filters[index].operator.message}</div>}
              </div>
              <div className="form-group col">
                <div className="d-flex">
                  <div className="w-100">
                    {(() => {
                      const field = getFilterField(index);
                      if (field?.available_values) {
                        const options = Object.entries(field.available_values).map(([value, translationKey]) => ({
                          value,
                          label: editableTranslate(translationKey, periodActivityTranslationFallback(translationKey))
                        }));

                        return (
                          <Controller
                            name={`filters.${index}.value`}
                            control={control}
                            render={({ field: { onChange, onBlur, value, ref } }) => (
                              <div style={{ display: 'contents' }} data-testid="filter-value-select">
                                <Select
                                  error={!!errors?.filters?.[index]?.value}
                                  options={options}
                                  isClearable={true}
                                  onChange={(opt) => onChange(opt ? opt.value : '')}
                                  onBlur={onBlur}
                                  ref={ref}
                                  value={options.find(o => o.value === value) || null}
                                  placeholder={editableTranslate('form.select_placeholder')}
                                />
                              </div>
                            )}
                          />
                        );
                      }

                      if (field?.type === 'date') {
                        return <DateInput className="col" error={!!errors?.filters?.[index]?.value} {...register(`filters.${index}.value`)} maxLength={255} />;
                      }

                      return <Field error={errors?.filters?.[index]?.value?.message}><Input type="text" className="col" error={!!errors?.filters?.[index]?.value} {...register(`filters.${index}.value`)} maxLength={255} /></Field>;
                    })()}
                  </div>
                  <div className="col-auto d-flex action-btn-div pl-2 pr-0">
                    <Button onClick={() => handleRemoveRow(index)} variant="ghost" size="inline" icon="bi-x-square-fill" className="btn-remove-filter" data-testid="cancel-button" />
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="text-center">
            <Button onClick={() => append({ value: '' })} variant="ghost" size="inline" icon="bi-plus" className="font-weight-bold red-dark" data-testid="add-condition-button">{editableTranslate('filters.add_condition')}</Button>
          </div>
        </form>
      </div>
      <div className="d-flex py-3 px-3 mt-3 filters-footer justify-content-between">
        <Button data-testid="back-button" as={Link} variant="ghost" size="inline" icon="bi-arrow-left" to="/settings/actions" className="mr-2">
          {editableTranslate('button.back', 'Zurück')}
        </Button>
        <Button variant="secondary" onClick={handleApply} loading={loading} data-testid="apply-filters-button">{applyLabel || editableTranslate('button.apply_filters')}</Button>
      </div>
    </div>
  );
}
